"use client";

import { NextIntlClientProvider, useLocale, useMessages } from "next-intl";
import { type ReactNode, useEffect, useState } from "react";
import { loadDemoBundle } from "@/lib/use-demo-translator";
import { useWidgetConfig } from "@/lib/use-widget-config";

/**
 * Demo content lives in separate `messages/demo/<locale>.json` files
 * that are **not** in the always-loaded i18n bundle — production
 * visitors never download them. This wrapper dynamically imports the
 * demo namespace and merges it into the next-intl provider ONLY when
 * the widget runs in demo mode (`isDummy` — the operator dashboard
 * preview / marketing site). In live mode the demo builders are never
 * called, so the namespace isn't needed and nothing extra is fetched.
 */
export function DemoMessages({ children }: { children: ReactNode }) {
  const { isDummy } = useWidgetConfig();
  const locale = useLocale();
  const base = useMessages();
  // A caller may already supply a NON-EMPTY demo namespace (the Next SSR
  // i18n config merges it in for the iframe build; tests sometimes pre-
  // merge it too). The lib bundles ship an empty `demo: {}` stub to
  // satisfy `useTranslations("demo")`'s hook contract, so we explicitly
  // check for "stub or absent" → lazy-load, "real content" → passthrough.
  const baseDemo = (base as Record<string, unknown>).demo;
  const baseHasDemo =
    baseDemo !== undefined &&
    typeof baseDemo === "object" &&
    baseDemo !== null &&
    Object.keys(baseDemo).length > 0;
  const [demo, setDemo] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (!isDummy || baseHasDemo) return;
    let active = true;
    loadDemoBundle(locale).then((m) => {
      if (active) setDemo(m);
    });
    return () => {
      active = false;
    };
  }, [isDummy, baseHasDemo, locale]);

  // Live / production — pass through unchanged (no extra fetch, no wrapper).
  if (!isDummy) return <>{children}</>;
  // Demo mode — ALWAYS render exactly ONE provider wrapper so the tree
  // structure stays STABLE across a runtime locale swap. The SSR base already
  // carries `demo` (baseHasDemo), but after a mid-session language change the
  // RUNTIME base ships an empty `demo: {}` stub → baseHasDemo flips false. If
  // we passed children through in one case and wrapped them in the other, that
  // flip would REPARENT `children` and REMOUNT the whole widget — closing an
  // open panel (the "changing language closes the widget" bug). A stable
  // wrapper keeps the widget mounted; only the messages prop changes.
  const merged = baseHasDemo ? base : demo ? { ...base, demo } : null;
  // Only the very first pre-load frame in demo mode (before either the SSR
  // demo or the lazy import is available) holds the tree.
  if (!merged) return null;
  return (
    <NextIntlClientProvider
      locale={locale}
      messages={merged}
      // Inherit the visitor's browser zone; UTC fallback for SSR.
      // Silences next-intl's ENVIRONMENT_FALLBACK warning when this
      // wrapper kicks in (demo mode + lazy demo namespace just landed).
      timeZone={
        typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC"
      }
    >
      {children}
    </NextIntlClientProvider>
  );
}
